import React, { useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------
// EASY-TO-TUNE NARRATION TIMING
// ---------------------------------------------------------------------
// hoverDwellMs: how long the cursor must sit still over a hinted element
//   before she comments on it. Higher = less chatty/accidental triggers.
// hideDelayMs: small grace period after the cursor leaves before the
//   bubble disappears, so briefly overshooting the element's edge
//   doesn't cause visible flicker.
// maxDisplayMs: safety auto-hide, in case the cursor sits on one spot
//   for a very long time — keeps her from talking forever about one
//   thing. Set to 0 to disable (bubble stays until you move away).
const NARRATION_CONFIG = {
  hoverDwellMs: 1200,
  hideDelayMs: 300,
  maxDisplayMs: 8000,
};

// ---------------------------------------------------------------------
// AUDIO SETTINGS
// ---------------------------------------------------------------------
// volume: 0.0–1.0, applied to every narration line.
// interruptOnNewHint: if the cursor moves to a new hinted element while
//   a line is still playing, should the old line be cut off immediately
//   (true) or allowed to finish before the new one starts (false)?
//
// IMPORTANT BROWSER LIMITATION: browsers block audio from playing
// automatically until the user has interacted with the page at least
// once (a click, a keypress, a tap) — a mouse hover alone typically does
// NOT count. This means the very first hover-triggered line on a fresh
// page load may fail to play silently. This is a browser policy, not a
// bug here — nothing to fix, it resolves itself after the user's first
// click anywhere on the page. Playback failures are caught and logged
// as a console warning rather than thrown, so they never break the UI.
const AUDIO_CONFIG = {
  volume: 1.0,
  interruptOnNewHint: true,
};

// ---------------------------------------------------------------------
// CLICK-TRIGGERED LINES (e.g. from SpineCompanion's hotspots)
// ---------------------------------------------------------------------
// displayMsWithoutAudio: how long a click-triggered line stays visible
//   when it has no voice recording yet — with a recording, the bubble
//   instead hides itself right when the audio finishes, which times out
//   more naturally than a fixed duration.
// eventName: must match the CustomEvent name SpineCompanion (or anything
//   else) dispatches to trigger a line. Change both sides if you rename.
const CLICK_SPEECH_CONFIG = {
  displayMsWithoutAudio: 4000,
  eventName: 'companion-speak',
};

// ---------------------------------------------------------------------
// EASY-TO-TUNE BUBBLE POSITION / STYLE
// ---------------------------------------------------------------------
// This is positioned independently of SpineCompanion's own container —
// it just assumes she sits in the bottom-right corner (matching
// SpineCompanion's current CSS) and floats the bubble above that area.
// Adjust `bottom`/`right` to taste if you reposition her.
const BUBBLE_STYLE = {
  position: 'fixed',
  bottom: '625px',
  right: '50px',
  maxWidth: '320px',
  zIndex: 10000,
};

/**
 * Usage: mark any element anywhere on the page with a hint, e.g.
 *   <a href="/newsletter"
 *      data-companion-hint="That's our newsletter link! Make sure to sign up for frequent updates!"
 *      data-companion-audio="/audio/newsletter-hint.mp3">
 *     Newsletter
 *   </a>
 * data-companion-audio is entirely optional — omit it on any element that
 * doesn't have a voice line yet (e.g. before voice acting is recorded);
 * the bubble still shows text-only in that case. This lets you add voice
 * lines incrementally without needing one for every hint right away.
 *
 * Separately, anything else on the page (e.g. SpineCompanion's clickable
 * hotspots) can trigger a line on demand — no hover or data attribute
 * needed — by dispatching:
 *   window.dispatchEvent(new CustomEvent('companion-speak', {
 *     detail: { text: "That's our newsletter!", audioSrc: '/audio/x.mp3' }
 *   }));
 * `audioSrc` is optional there too. This is how SpineCompanion's hotspots
 * talk to this component without either file importing the other.
 *
 * Then render <CompanionNarrator /> once, anywhere in the app (e.g. as a
 * sibling next to <SpineCompanion />). It handles everything else.
 */
const CompanionNarrator = () => {
  const [visibleText, setVisibleText] = useState(null);

  const dwellTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const maxDisplayTimerRef = useRef(null);
  const currentElementRef = useRef(null);
  const audioRef = useRef(null); // the single <audio> element we reuse for every line

  useEffect(() => {
    // Single reusable <audio> element for every narration line, rather
    // than creating a new Audio() per hint — simpler to stop/reset when
    // a hint changes or the bubble hides early.
    const audio = new Audio();
    audio.volume = AUDIO_CONFIG.volume;
    audioRef.current = audio;

    const stopAudio = () => {
      audio.onended = null;
      audio.pause();
      audio.currentTime = 0;
    };

    // `onEnded` (optional): called when this line's audio finishes
    // naturally. Used by the click-triggered flow to hide the bubble
    // exactly when the voice line ends, rather than on a fixed timer —
    // not used by the hover flow, which hides on mouseout instead.
    const playAudio = (src, onEnded) => {
      if (!src) return false; // no voice line for this hint yet — text-only is fine
      if (AUDIO_CONFIG.interruptOnNewHint) stopAudio();
      audio.src = src;
      audio.onended = onEnded || null;
      // play() returns a promise that rejects if the browser's autoplay
      // policy blocks it (most commonly: no user gesture yet this page
      // load). That's expected and not an error in our code, so we just
      // warn instead of letting an unhandled rejection surface.
      audio.play().catch((err) => {
        console.warn(
          'Companion audio blocked or failed to play (this is often just the browser\'s autoplay policy waiting for a user click/keypress first):',
          err.message
        );
      });
      return true;
    };

    const clearAllTimers = () => {
      clearTimeout(dwellTimerRef.current);
      clearTimeout(hideTimerRef.current);
      clearTimeout(maxDisplayTimerRef.current);
    };

    const showHint = (text, audioSrc) => {
      clearTimeout(hideTimerRef.current);
      setVisibleText(text);
      playAudio(audioSrc);

      if (NARRATION_CONFIG.maxDisplayMs > 0) {
        clearTimeout(maxDisplayTimerRef.current);
        maxDisplayTimerRef.current = setTimeout(() => {
          setVisibleText(null);
          stopAudio();
        }, NARRATION_CONFIG.maxDisplayMs);
      }
    };

    const scheduleHide = () => {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        setVisibleText(null);
        stopAudio();
      }, NARRATION_CONFIG.hideDelayMs);
    };

    const handleMouseOver = (e) => {
      const hintElement = e.target.closest('[data-companion-hint]');
      if (!hintElement) return;
      if (hintElement === currentElementRef.current) return; // already tracking this one

      // Moved to a new hinted element — cancel any pending hide/dwell
      // from whatever we were previously tracking, and start fresh.
      clearAllTimers();
      currentElementRef.current = hintElement;

      const text = hintElement.getAttribute('data-companion-hint');
      const audioSrc = hintElement.getAttribute('data-companion-audio'); // may be null — that's fine
      dwellTimerRef.current = setTimeout(() => {
        showHint(text, audioSrc);
      }, NARRATION_CONFIG.hoverDwellMs);
    };

    const handleMouseOut = (e) => {
      const hintElement = e.target.closest('[data-companion-hint]');
      if (!hintElement || hintElement !== currentElementRef.current) return;

      // Only treat this as "really left" if the cursor didn't just move
      // to a child element still inside the same hinted element.
      if (hintElement.contains(e.relatedTarget)) return;

      currentElementRef.current = null;
      clearTimeout(dwellTimerRef.current);
      scheduleHide();
    };

    // Click-triggered lines (e.g. SpineCompanion's hotspots) arrive as a
    // CustomEvent rather than through hover, so they get their own path
    // rather than going through showHint/scheduleHide above. A click
    // always takes priority over whatever hover state was in progress.
    const handleSpeakEvent = (e) => {
      const detail = e.detail || {};
      const { text, audioSrc, displayMs } = detail;
      if (!text) return;

      // Cancel any in-progress hover dwell/hide so the two systems never
      // fight over the bubble; the clicked line always wins immediately.
      clearAllTimers();
      currentElementRef.current = null;

      setVisibleText(text);

      const playedAudio = playAudio(audioSrc, () => {
        // Bubble disappears right as the voice line finishes — more
        // natural than guessing a fixed duration for every line.
        setVisibleText(null);
      });

      if (!playedAudio) {
        // No audio for this line (not recorded yet, or none given) —
        // fall back to a plain timed display instead.
        hideTimerRef.current = setTimeout(() => {
          setVisibleText(null);
        }, displayMs ?? CLICK_SPEECH_CONFIG.displayMsWithoutAudio);
      }

      // Overall safety cap either way, in case audio hangs or is much
      // longer than expected.
      if (NARRATION_CONFIG.maxDisplayMs > 0) {
        clearTimeout(maxDisplayTimerRef.current);
        maxDisplayTimerRef.current = setTimeout(() => {
          setVisibleText(null);
          stopAudio();
        }, NARRATION_CONFIG.maxDisplayMs);
      }
    };

    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);
    window.addEventListener(CLICK_SPEECH_CONFIG.eventName, handleSpeakEvent);

    return () => {
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      window.removeEventListener(CLICK_SPEECH_CONFIG.eventName, handleSpeakEvent);
      clearAllTimers();
      stopAudio();
      audioRef.current = null;
    };
  }, []);

  return (
    <div
      style={{
        ...BUBBLE_STYLE,
        opacity: visibleText ? 1 : 0,
        transform: visibleText ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 0.25s ease, transform 0.25s ease',
        pointerEvents: 'none',
      }}
    >
      {visibleText && (
        <div
          style={{
            background: 'white',
            color: '#222',
            borderRadius: '14px',
            padding: '10px 14px',
            fontSize: '14px',
            lineHeight: 1.4,
            boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
          }}
        >
          {visibleText}
        </div>
      )}
    </div>
  );
};

export default CompanionNarrator;
